import { prepareHeaders } from '@/lib/cookie'
import { BASE_URL } from '@/constant'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

export interface WidgetSummary {
  id: number
  name: string
  description: string | null
  component_key: string
}

export interface WidgetGroup {
  group_id: number
  group_name: string
  widgets: WidgetSummary[]
}

export interface MyWidgetsResponse {
  success: boolean
  data: WidgetGroup[]
}

export interface RoleWidget extends WidgetSummary {
  assigned: number
}

export interface RoleWidgetGroup {
  group_id: number
  group_name: string
  widgets: RoleWidget[]
}

export interface RoleWidgetsResponse {
  success: boolean
  data: RoleWidgetGroup[]
}

export interface UserWidget extends WidgetSummary {
  role_granted: number
  override_enabled: number | null
}

export interface UserWidgetGroup {
  group_id: number
  group_name: string
  widgets: UserWidget[]
}

export interface UserWidgetsResponse {
  success: boolean
  data: UserWidgetGroup[]
}

export interface SetRoleWidgetsPayload {
  roleId: number
  widget_ids: number[]
}

export interface SetUserWidgetsPayload {
  userId: number
  overrides: { widget_id: number; is_enabled: boolean | null }[]
}

export const widgetsApi = createApi({
  reducerPath: 'widgetsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_URL}/widgets`,
    prepareHeaders: prepareHeaders,
  }),
  tagTypes: ['MyWidgets', 'RoleWidgets', 'UserWidgets'],
  endpoints: builder => ({
    getMyWidgets: builder.query<MyWidgetsResponse, void>({
      query: () => '/mine',
      providesTags: ['MyWidgets'],
    }),
    getRoleWidgets: builder.query<RoleWidgetsResponse, number>({
      query: roleId => `/roles/${roleId}`,
      providesTags: ['RoleWidgets'],
    }),
    setRoleWidgets: builder.mutation<
      { success: boolean; message: string },
      SetRoleWidgetsPayload
    >({
      query: ({ roleId, widget_ids }) => ({
        url: `/roles/${roleId}`,
        method: 'PUT',
        body: { widget_ids },
      }),
      invalidatesTags: ['RoleWidgets', 'MyWidgets'],
    }),
    getUserWidgets: builder.query<UserWidgetsResponse, number>({
      query: userId => `/users/${userId}`,
      providesTags: ['UserWidgets'],
    }),
    setUserWidgets: builder.mutation<
      { success: boolean; message: string },
      SetUserWidgetsPayload
    >({
      query: ({ userId, overrides }) => ({
        url: `/users/${userId}`,
        method: 'PUT',
        body: { overrides },
      }),
      invalidatesTags: ['UserWidgets', 'MyWidgets'],
    }),
  }),
})

export const {
  useGetMyWidgetsQuery,
  useGetRoleWidgetsQuery,
  useSetRoleWidgetsMutation,
  useGetUserWidgetsQuery,
  useSetUserWidgetsMutation,
} = widgetsApi
